/* eslint-disable @typescript-eslint/no-require-imports */
/* eslint-disable import/no-webpack-loader-syntax */
import { $t } from '@/i18n';
import { registerPreloadCredentials, usesArtifactCredentials } from '@/utils/aiArtifactCredentials';
import { memoize } from 'lodash';
import { createRouterForGogo } from './vue3router';
import { createGatedGogoRouteView } from './GatedGogoRouteView';
import { loadEditorCdn, fetchEditorRemoteCdnUrl } from './editor-cdn-loader';
import { showEditorLoadError } from './showEditorLoadError';
import { readGogoRemotePreviewConnection } from './gogoRemotePreviewConnection';
export const GOGO_CTX_MODULES_KEY = '__GOGO_CTX_MODULES__';
export const loadVue3Stuff = memoize(async () => {
    // @ts-ignore
    const $vue3part2 = import('@tencent/wuji-vue-next').then(x => x.default || x);
    const { moduleManage } = window.wujiComp;
    const { allWebpackExternals } = moduleManage;
    if (!allWebpackExternals.vue3)
        await moduleManage.loadVue3Dependencies();
    // @ts-ignore
    const tdesign = await import('@tencent/wuji-vue-next/dist/tdesign.es.js');
    const vue3part2 = await $vue3part2;
    const routerInstance = createRouterForGogo(vue3part2.vueRouter, allWebpackExternals.vue3);
    const ans = {
        routerInstance,
        vue: allWebpackExternals.vue3,
        vueRouter: vue3part2.vueRouter, // 在 allWebpackExternals 没有 —— 等后面 maoyi 看看
        'ant-design-vue': allWebpackExternals.vue3Antd,
        'tdesign-vue-next': tdesign,
        Vue3Adaptor: allWebpackExternals.Vue3Adaptor,
    };
    const w = window;
    w[GOGO_CTX_MODULES_KEY] = {
        ...(w[GOGO_CTX_MODULES_KEY] || {}),
        'ant-design-vue': ans['ant-design-vue'],
        'tdesign-vue-next': ans['tdesign-vue-next'],
    };
    for (const [key, value] of Object.entries(ans)) {
        window.define(`gogo-env/${key}`, [], () => value);
    }
    return ans;
});
if (typeof XY_GPT_HOST_URL === 'string') {
    // 这段代码只会在开发环境下有（开发环境才有 XY_GPT_HOST_URL 环境变量），所以应该是安全的
    let gptHostUrl = XY_GPT_HOST_URL;
    const port = /:(\d+)/.exec(gptHostUrl)?.[1] || '5173';
    if (/\d+\.\w+\.cloudide\.woa\.com/.test(window.location.origin)) {
        // 龙虾开发机的地址类似 XXXXX-1024.gz.cloudide.woa.com 这种
        gptHostUrl = window.location.origin.replace(/-\d+/, `-${port}`);
    }
    else if (window.location.origin.includes('devcloud.woa.com')) {
        // 开发云的地址类似 XXXXX.devcloud.woa.com:1024 这种
        gptHostUrl = window.location.origin.replace(/(:\d+)?$/, `:${port}`);
    }
    window.GPT_HOST_URL = gptHostUrl;
}
const getWujiGogoEditorFn = () => window.getWujiGogoEditor;
const waitForGogoEditorFn = () => {
    const existing = getWujiGogoEditorFn();
    if (typeof existing === 'function') {
        return Promise.resolve(existing);
    }
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50;
        const timer = setInterval(() => {
            attempts += 1;
            const getEditor = getWujiGogoEditorFn();
            if (typeof getEditor === 'function') {
                clearInterval(timer);
                resolve(getEditor);
                return;
            }
            if (attempts >= maxAttempts) {
                clearInterval(timer);
                reject(new Error(`[xy-client] getWujiGogoEditor not available after ${maxAttempts} attempts`));
            }
        }, 100);
    });
};
const waitForEditorInit = async (vue) => {
    const getEditor = await waitForGogoEditorFn();
    // 首次调用会同步执行整个 for-cdn 包体。微任务仍算进当前宏任务（含 setInterval），
    // 必须切到下一个宏任务，否则 Chrome 会报 Violation。
    await new Promise((resolve) => {
        setTimeout(resolve, 0);
    });
    return getEditor({ vue });
};
const gogoLibMapSource = {
    editor: async () => {
        const gogoEditorVersion = require('@tencent/wuji-gogo-editor/package.json')?.version || '';
        const serveSubPath = XY_USE_LOCAL_GOGO ? 'gogo-editor-dev' : `gogo-editor-${gogoEditorVersion}`;
        const baseUrl = `${window.GLOBAL_INFO.staticFileCdn || window.GLOBAL_INFO.pathPrefix}/xystatic/${serveSubPath}`;
        const $setupShiki = import('./setupShiki').then(x => x.setupShiki);
        let js;
        try {
            const { vue } = await loadVue3Stuff();
            js = await loadEditorCdn(baseUrl, () => waitForEditorInit(vue));
        }
        catch (e) {
            return showEditorLoadError({ baseUrl, editorVersion: gogoEditorVersion, error: e });
        }
        const setupShiki = await $setupShiki;
        setupShiki(js.monaco);
        return js;
    },
    editorRemote: async () => {
        const gogoEditorRemoteVersion = require('@tencent/wuji-gogo-editor-remote/package.json')?.version || '';
        const serveSubPath = (XY_USE_LOCAL_GOGO || XY_USE_LOCAL_GOGO_REMOTE) ? 'gogo-editor-dev-remote' : `gogo-editor-remote-${gogoEditorRemoteVersion}`;
        const baseUrl = `${window.GLOBAL_INFO.staticFileCdn || window.GLOBAL_INFO.pathPrefix}/xystatic/${serveSubPath}`;
        let js;
        try {
            const { vue } = await loadVue3Stuff();
            // 滚动发布地址走 manager /container/editor-remote-url（按 major + devenvMajor 匹配），失败兜底本地版本化目录
            js = await loadEditorCdn(baseUrl, () => waitForEditorInit(vue), { fetchManifestUrl: fetchEditorRemoteCdnUrl });
            if (!js?.GogoEditorRemote) {
                throw new Error('[xy-client] GogoEditorRemote export missing from editor-remote bundle');
            }
        }
        catch (e) {
            return showEditorLoadError({ baseUrl, editorVersion: 'remote', error: e });
        }
        return js;
    },
    runtime: async () => {
        await loadVue3Stuff(); // 确保 vue3 ready
        const [js] = await Promise.all([
            import('@tencent/wuji-gogo-runtime'),
            // @ts-ignore - style import may not have types
            import(/* webpackChunkName: "gogo-runtime-css" */ '@tencent/wuji-gogo-runtime/index.css'),
        ]);
        return js;
    },
};
export const loadGogoLib = memoize(async (key) => gogoLibMapSource[key]());
// 创建 Pinia installer plugin，确保每个 Vue3Adaptor 实例拥有独立的 Pinia
const createPiniaPlugin = (pinia) => ({
    install(app) {
        app.use(pinia.createPinia());
    },
});
/**
 * 一个 Vue2 异步的组件，提供了 GogoEditor
 *
 * see https://v2.vuejs.org/v2/guide/components-dynamic-async#Handling-Loading-State
 */
export const AsyncGogoEditor = memoize(() => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('editor');
        return stuff.Vue3Adaptor.default(gogo.GogoEditor, { library: 'tdesign', plugins: [createPiniaPlugin(gogo.pinia)] });
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
          <div class='animate-fade-in mt-4 text-gray-8'>{$t('k_c33419b7')}</div>
        </div>;
            },
        },
    };
});
export const AsyncGogoEditorRemote = memoize(() => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('editorRemote');
        return stuff.Vue3Adaptor.default(gogo.GogoEditorRemote, { library: 'tdesign', plugins: [createPiniaPlugin(gogo.pinia)] });
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
          <div class='animate-fade-in mt-4 text-gray-8'>{$t('k_c33419b7')}</div>
        </div>;
            },
        },
    };
});
/**
 * 一个 Vue2 异步的组件，提供了 GogoEditor 的 Entrance 界面
 */
export const AsyncGogoEditorEntrance = memoize(() => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('editor');
        window.appendTextToStartGuide = gogo.appendTextToStartGuide;
        window.setTextToStartGuide = gogo.setTextToStartGuide;
        return stuff.Vue3Adaptor.default(gogo.StartGuide, { library: 'tdesign', plugins: [createPiniaPlugin(gogo.pinia)] });
    })();
    return {
        component,
        loading: {
            render() {
                return <div class='w-full h-full bg-white'></div>;
            },
        },
    };
});
/**
 * 一个 Vue2 异步的组件，提供了 gogo-runtime 下面的东西
 *
 * see https://v2.vuejs.org/v2/guide/components-dynamic-async#Handling-Loading-State
 */
const getGogoRuntimeComponent = memoize((exportName) => {
    const component = (async () => {
        const stuff = await loadVue3Stuff();
        const gogo = await loadGogoLib('runtime');
        const { pinia } = gogo;
        const router = stuff.routerInstance;
        const target = exportName === 'GogoRouteView'
            ? createGatedGogoRouteView(stuff.vue, stuff.vueRouter, gogo.GogoRouteView)
            : gogo[exportName];
        const comp = stuff.Vue3Adaptor.default(target, { library: 'tdesign', plugins: [router, pinia] });
        return comp;
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
        </div>;
            },
        },
    };
});
export const AsyncGogoHost = () => getGogoRuntimeComponent('GogoHost');
export const AsyncGogoRouteView = () => getGogoRuntimeComponent('GogoRouteView');
// --- React Runtime ---
export const loadReactStuff = memoize(async () => {
    const [gogoReactRuntime, 
    // @ts-ignore
    ReactModule, 
    // @ts-ignore
    ReactDOM, 
    // @ts-ignore
    ReactDOMClient, 
    // @ts-ignore
    ReactRouterDom,] = await Promise.all([
        import('@tencent/wuji-gogo-runtime-react'),
        // @ts-ignore
        import('@tencent/wuji-react/react'),
        // @ts-ignore
        import('@tencent/wuji-react/react-dom'),
        // @ts-ignore
        import('@tencent/wuji-react/react-dom/client'),
        // @ts-ignore
        import('@tencent/wuji-react/react-router-dom'),
    ]);
    return { gogoReactRuntime, ReactModule, ReactDOM, ReactDOMClient, ReactRouterDom };
});
/**
 * 轻量 React-in-Vue2 桥接组件。
 * 使用 React 19 的 createRoot API 挂载 React 组件，
 * watch props 变化时 re-render，beforeDestroy 时 unmount。
 */
function createReactBridge(ReactModule, ReactDOMClient, ReactComponent) {
    const { createElement: h } = ReactModule;
    function renderReact(vm) {
        if (!vm.$refs.container)
            return;
        if (!vm._reactRoot) {
            vm._reactRoot = ReactDOMClient.createRoot(vm.$refs.container);
        }
        vm._reactRoot.render(h(ReactComponent, vm.reactProps));
    }
    return {
        name: 'ReactBridge',
        props: {
            reactProps: { type: Object, default: () => ({}) },
        },
        render(createElement) {
            return createElement('div', { ref: 'container', style: 'display:contents' });
        },
        mounted() {
            renderReact(this);
        },
        watch: {
            reactProps: {
                deep: true,
                handler() {
                    renderReact(this);
                },
            },
        },
        beforeDestroy() {
            if (this._reactRoot) {
                this._reactRoot.unmount();
                this._reactRoot = null;
            }
        },
    };
}
function createReactGogoHostApp(ReactModule, ReactDOM, ReactRouterDom, ReactGogoHost, ReactGogoRouteView, useGogoHost) {
    const { createElement: h, useEffect, useRef, createContext, useContext } = ReactModule;
    const { createPortal } = ReactDOM;
    const { createMemoryRouter, RouterProvider } = ReactRouterDom;
    const vue2Router = window.xy_runtime_router;
    function getFullPath(loc) {
        return (loc.pathname || '/') + (loc.search || '') + (loc.hash || '');
    }
    function HostStatusBridge({ onHostStatusChanged }) {
        const host = useGogoHost();
        const prevStatusRef = useRef(null);
        useEffect(() => {
            if (!host || host.hostStatus === prevStatusRef.current)
                return;
            prevStatusRef.current = host.hostStatus;
            onHostStatusChanged?.(host.hostStatus, host.exports);
        }, [host?.hostStatus, host?.exports]);
        return null;
    }
    function PortalledRouteView({ container }) {
        if (!container)
            return null;
        return createPortal(h(ReactGogoRouteView, { notFound: h('div', null, 'Page not found') }), container);
    }
    const PropsContext = createContext(null);
    function ReactGogoHostAppInner() {
        const props = useContext(PropsContext);
        return h(ReactGogoHost, {
            content: props.content,
            prepareEnv: props.prepareEnv,
            debugAllowPicking: props.debugAllowPicking,
            debugIsErrorReportable: props.debugIsErrorReportable,
            debugBridgeInstanceName: props.debugBridgeInstanceName,
            onReady: props.onReady,
        }, h(HostStatusBridge, { onHostStatusChanged: props.onHostStatusChanged }), h(PortalledRouteView, { container: props.routeViewContainer }));
    }
    // 创建 React MemoryRouter 并建立与 Vue2 Router 的双向同步（类似 Vue3 链路的钩子方式）
    const initialPath = vue2Router?.currentRoute?.fullPath || '/';
    const reactRouter = createMemoryRouter([{ path: '/*', Component: ReactGogoHostAppInner }], { initialEntries: [initialPath] });
    let isSyncing = false;
    // React → Vue2
    reactRouter.subscribe((state) => {
        if (isSyncing || !vue2Router)
            return;
        const reactPath = getFullPath(state.location);
        if (vue2Router.currentRoute.fullPath !== reactPath) {
            isSyncing = true;
            vue2Router.push(reactPath).catch(() => { });
            Promise.resolve().then(() => {
                isSyncing = false;
            });
        }
    });
    // Vue2 → React
    if (vue2Router) {
        vue2Router.afterEach((to) => {
            if (isSyncing)
                return;
            const reactPath = getFullPath(reactRouter.state.location);
            if (reactPath !== to.fullPath) {
                isSyncing = true;
                reactRouter.navigate(to.fullPath, { replace: true });
                Promise.resolve().then(() => {
                    isSyncing = false;
                });
            }
        });
    }
    function ReactGogoHostApp(props) {
        return h(PropsContext.Provider, { value: props }, h(RouterProvider, { router: reactRouter }));
    }
    ReactGogoHostApp.displayName = 'ReactGogoHostApp';
    return { ReactGogoHostApp };
}
export const AsyncReactGogoHost = memoize(() => {
    const component = (async () => {
        const { gogoReactRuntime, ReactModule, ReactDOM, ReactDOMClient, ReactRouterDom } = await loadReactStuff();
        const { ReactGogoHost, ReactGogoRouteView, useGogoHost } = gogoReactRuntime;
        const { ReactGogoHostApp } = createReactGogoHostApp(ReactModule, ReactDOM, ReactRouterDom, ReactGogoHost, ReactGogoRouteView, useGogoHost);
        return createReactBridge(ReactModule, ReactDOMClient, ReactGogoHostApp);
    })();
    return {
        component,
        delay: 300,
        loading: {
            render() {
                return <div class='flex flex-col items-center justify-center'>
          <t-loading />
        </div>;
            },
        },
    };
});
// --- Remote Runtime（Vue3，通过远端 Vite dev server 加载用户代码并支持 HMR）---
export const loadGogoRemoteStuff = memoize(async () => {
    const stuff = await loadVue3Stuff(); // 确保 vue3 ready
    const [lib] = await Promise.all([
        // @ts-ignore - 走 common.config.js 里的 alias 指向本地 runtime-remote/lib
        import('@tencent/wuji-gogo-runtime-remote'),
        // @ts-ignore - 构建产物未自带样式，需单独引入
        import(/* webpackChunkName: "gogo-runtime-remote-css" */ '@tencent/wuji-gogo-runtime-remote/wuji-gogo-runtime-remote.css'),
    ]);
    return { stuff, lib };
});
function invokeVue3PickHandler(handler, e) {
    if (!handler)
        return;
    const fn = typeof handler === 'function'
        ? handler
        : handler.value;
    fn?.(e);
}
function createRemoteHostFacade(controller) {
    return {
        controller,
        context: controller.context,
        handlePointerDown: (e) => {
            invokeVue3PickHandler(controller.context?.handlePointerDown, e);
        },
        abortEventIfPicking: (e) => {
            invokeVue3PickHandler(controller.context?.abortEventIfPicking, e);
        },
    };
}
/**
 * 组合一个 Vue3 组件：宿主 GogoRemoteHost 常驻，RouteView 通过 Teleport 投射到
 * 每个路由 RouterView 提供的 DOM 容器中（对应 React 链路里的 createPortal）。
 * 这样宿主只挂载一次、跨路由存活，而 RouteView 跟随 Vue2 路由切换。
 *
 * 与 GogoHost 共用 loadVue3Stuff 的 routerInstance，路由同步由 vue3router 统一处理。
 */
function createGogoRemoteHostApp(vue, lib, router) {
    const { h, defineComponent, Teleport, onMounted, onUnmounted } = vue;
    const { GogoRemoteHost, GogoRemoteRouteView } = lib;
    return defineComponent({
        name: 'GogoRemoteHostApp',
        props: {
            wsUrl: { type: String, default: '' },
            src: { type: String, default: '' },
            previewBridge: { type: Boolean, default: true },
            beforeLoadRemoteModule: { type: Function, default: undefined },
            routeViewContainer: { default: null },
        },
        emits: ['ready', 'statusChanged'],
        setup(props, { emit }) {
            const bridgeDisposers = [];
            let bridgeDisposed = false;
            let bridgeHandlePromise = null;
            // 挂在 window.__GOGO_CTX__.softNavigate 上，而不是透传给 startDevenvBridge：
            // DomBridge 与 Playwright bridge（后者通过 page.evaluate 直接读同一个 window）
            // 都从这里取宿主路由钩子，不必分别接入。与 wsUrl/startBridge 无关，尽早注册。
            let softNavigateRegistered = false;
            function registerGogoSoftNavigate() {
                if (softNavigateRegistered)
                    return;
                softNavigateRegistered = true;
                try {
                    lib.injectSharedGlobals();
                    lib.setGogoSoftNavigate(gogoSoftNavigate);
                    bridgeDisposers.push(() => lib.setGogoSoftNavigate(null));
                }
                catch (e) {
                    console.warn('[gogo-runtime-remote] failed to register softNavigate', e);
                }
            }
            const gogoSoftNavigate = (({ path, replace }) => {
                const base = router.options.history.base.replace(/\/$/, '');
                const routePath = path === base || path.startsWith(`${base}/`)
                    ? path.slice(base.length) || '/'
                    : path;
                const { matched } = router.resolve(routePath);
                if (!matched.length) {
                    return false;
                }
                return (replace ? router.replace(routePath) : router.push(routePath)).then(() => true);
            });
            function startBridge() {
                if (!props.wsUrl)
                    return null;
                if (!bridgeHandlePromise) {
                    bridgeHandlePromise = (async () => {
                        // @ts-ignore - 该包按需安装，类型可能缺失
                        const { startDevenvBridge } = await import('@tencent/wuji-gogo-runtime-remote/devenv-bridge');
                        const { browserId } = readGogoRemotePreviewConnection();
                        const handle = await startDevenvBridge({
                            wsUrl: props.wsUrl,
                            browserIdBase: browserId || 'xy-runtime-remote',
                            autoSelectSession: true, // 自动选择会话
                            // @ts-ignore - 等九月份可以删除这段代码，新的runtime-remote包不用这个
                            softNavigate: gogoSoftNavigate,
                        });
                        if (bridgeDisposed) {
                            await handle.dispose();
                            return handle;
                        }
                        bridgeDisposers.push(() => handle.dispose());
                        return handle;
                    })().catch((e) => {
                        console.warn('[gogo-runtime-remote] failed to start devenv bridge', e);
                        throw e;
                    });
                }
                return bridgeHandlePromise;
            }
            const wsPreviewBridge = props.wsUrl
                ? {
                    createRemotePreviewBridgeHost: async (handlers) => {
                        try {
                            const handle = await startBridge();
                            return handle?.createWSRemotePreviewBridgeHost(handlers) ?? null;
                        }
                        catch {
                            return null;
                        }
                    },
                }
                : null;
            onMounted(async () => {
                registerGogoSoftNavigate();
                await startBridge()?.catch(() => undefined);
            });
            onUnmounted(() => {
                bridgeDisposed = true;
                const disposers = bridgeDisposers.splice(0); // 清空 disposers 数组
                for (const dispose of disposers) {
                    Promise.resolve(dispose?.()).catch(() => undefined); // 执行 disposer
                }
            });
            return () => h(GogoRemoteHost, {
                previewBridge: props.previewBridge // 通过网址里 uk-- 开启
                    || wsPreviewBridge // 通过 ws 连接
                    || false,
                src: props.src,
                crossOrigin: usesArtifactCredentials() ? 'use-credentials' : undefined,
                beforeLoadRemoteModule: async (src) => {
                    // 在入口执行前覆盖存量 Vite helper；登记实际加载地址，兼容代理预览。
                    registerPreloadCredentials(lib.toProxyUrl?.(src) ?? src);
                    await props.beforeLoadRemoteModule?.(src);
                },
                onReady: (controller) => emit('ready', createRemoteHostFacade(controller)),
                onStatusChanged: (status) => emit('statusChanged', status),
            }, {
                default: () => (props.routeViewContainer
                    ? h(Teleport, { to: props.routeViewContainer }, [
                        h(GogoRemoteRouteView),
                    ])
                    : null),
            });
        },
    });
}
export const AsyncGogoRemoteHost = memoize(() => {
    const component = (async () => {
        const { stuff, lib } = await loadGogoRemoteStuff();
        const GogoRemoteHostApp = createGogoRemoteHostApp(stuff.vue, lib, stuff.routerInstance);
        return stuff.Vue3Adaptor.default(GogoRemoteHostApp, { library: 'tdesign', plugins: [stuff.routerInstance] });
    })();
    return {
        component,
    };
});
